"use client";

import { useIsLoggedIn } from "@/lib/hooks/useIsLoggedIn";
import { createCheckoutSession } from "@/lib/stripe";
import { PlanCode } from "@/types/subscription";
import { Box, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState } from "react";
import ErrorAlert from "./ErrorAlert";
import PaymentCheck from "./PaymentCheck";
import { LoadingSmall } from "./Loading";
import { createNowPaymentsInvoice } from "@/lib/nowpayments";
import { usePaymentOptions, PaymentMethod } from "@/app/hooks/usePaymentOptions";

interface PaymentMethodsProps {
  selectedPlan: PlanCode;
  onPaymentComplete?: any;
  inviteCode?: string;
}

export default function PaymentMethods({ selectedPlan, onPaymentComplete, inviteCode }: PaymentMethodsProps) {
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const t = useTranslations("Membership");
  const tRoot = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // 使用共享的支付选项配置
  const paymentOptions = usePaymentOptions();

  const onPayment = async (method: PaymentMethod) => {
    if (isLoggedIn === false) {
      router.push(`/auth/signin?callbackUrl=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setLoading(true);

    try {
      setError(null);
      if (method === "creditCard") {
        const result = await createCheckoutSession(selectedPlan, inviteCode);
        if (result.success) {
          window.location.href = result.data.url;
        } else {
          setError(tRoot("Server.paymentInitFailed"));
        }
      } else if (method === "alipayWechat") {
        const result = await createCheckoutSession(selectedPlan, inviteCode, true);
        if (result.success) {
          window.location.href = result.data.url;
        } else {
          setError(tRoot("Server.paymentInitFailed"));
        }
      } else if (method === "crypto") {
        const result = await createNowPaymentsInvoice(selectedPlan, inviteCode);
        if (result.success) {
          window.location.href = result.data.url;
        } else {
          setError(tRoot("Server.paymentInitFailed"));
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      setError(tRoot("Server.paymentInitFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onPaymentError = async (errorCode: string) => {
    setError(tRoot(errorCode as any));
  };

  return (
    <Box my={3} id="_paymentMethod">
      <Typography variant="h4" fontSize="var(--font-size-xl)" fontWeight="600" textAlign="left" pb={3}>
        {t("payment.title")}
      </Typography>

      <PaymentCheck onPaymentComplete={onPaymentComplete} onPaymentError={onPaymentError} />

      {error && <ErrorAlert message={error} />}
      {loading && <LoadingSmall />}

      <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
        {paymentOptions.map((item, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => onPayment(item.method)}
            sx={{
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0.75rem",
              p: 3,
              color: "text.primary2",
              bgcolor: "background.paperMedium",
              cursor: "pointer",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "background.paperLight",
              },
            }}>
            <Typography
              variant="body1"
              fontWeight="600"
              sx={{ fontSize: "var(--font-size-s)", color: "text.primary2" }}>
              {item.label}
            </Typography>
            {item.icon}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
